<template>
  <div>
    <b-form-group label="display formula">
      <small class="text-muted d-block mb-2">Names each related row. Leave it empty to use the row id. Password and file columns are not offered.</small>
      <formula-editor
        :model-value="settings.displayFormula"
        :columns="formulaColumns"
        :spreadsheets="[]"
        :current-spreadsheet-unique-title="relatedSpreadSheet"
        @update:model-value="update('displayFormula', $event)"
      />
    </b-form-group>
    <b-form-group label="choices">
      <small class="text-muted d-block mb-2">Limit the rows a person can pick. A condition can compare a column on the related spreadsheet with a field on the row being edited.</small>
      <div v-for="(filter, index) in settings.cascadeFilters" :key="index" class="d-flex gap-2 mb-2 flex-wrap">
        <b-form-select :model-value="filter.field" :options="relatedFieldOptions" size="sm" @update:model-value="updateFilter(index, 'field', $event)" />
        <b-form-select :model-value="filter.operator" :options="operatorOptions(filter)" size="sm" @update:model-value="updateFilter(index, 'operator', $event)" />
        <b-form-select :model-value="filter.valueFromField || ''" :options="ownerFieldOptions" size="sm" @update:model-value="updateFilter(index, 'valueFromField', $event)" />
        <span class="choice-remove" @click="removeFilter(index)">x</span>
      </div>
      <div class="add-choice" @click="addFilter">+ add condition</div>
      <b-form-checkbox class="mt-2" :model-value="settings.showAllUntilParent" @update:model-value="update('showAllUntilParent', $event)">Show every related row until the parent has a value</b-form-checkbox>
    </b-form-group>
    <b-form-group label="add a missing row">
      <b-form-checkbox :model-value="settings.allowAdd" @update:model-value="update('allowAdd', $event)">Let a person add a related row from this field</b-form-checkbox>
      <div v-if="settings.allowAdd" class="mt-2">
        <small class="text-muted d-block mb-1">Required fields are always shown. Choose any extra fields. The new row has to match the choices above.</small>
        <b-form-checkbox
          v-for="column in extraColumns"
          :key="column.uniqueTitle"
          :model-value="settings.addFields.includes(column.uniqueTitle)"
          @update:model-value="toggleAddField(column.uniqueTitle, $event)"
        >{{ column.title || column.uniqueTitle }}</b-form-checkbox>
      </div>
    </b-form-group>
  </div>
</template>

<script>
import FormulaEditor from '../FormulaEditor.vue'
import { displayFormulaColumns, normalizeRelationshipSettings } from '../../../../shared/relationship-column.js'

export default {
  name: 'RelationshipColumnSettings',
  components: { FormulaEditor },
  props: {
    modelValue: { type: Object, default: () => ({}) },
    relatedColumns: { type: Array, default: () => [] },
    ownerColumns: { type: Array, default: () => [] },
    relatedSpreadSheet: { type: String, default: '' }
  },
  emits: ['update:modelValue'],
  computed: {
    settings () {
      return normalizeRelationshipSettings(this.modelValue)
    },
    formulaColumns () {
      return displayFormulaColumns(this.relatedColumns)
    },
    relatedFieldOptions () {
      return [{ value: '', text: 'related column' }].concat((this.relatedColumns || []).filter((column) => column.type !== 'password' && column.type !== 'file').map((column) => ({
        value: column.uniqueTitle,
        text: column.title || column.uniqueTitle
      })))
    },
    ownerFieldOptions () {
      const system = [
        { value: '', text: 'field on this row' },
        { value: 'id', text: 'ID' }
      ]
      return system.concat((this.ownerColumns || []).filter((column) => column.type !== 'password' && column.type !== 'file').map((column) => ({
        value: column.uniqueTitle,
        text: column.title || column.uniqueTitle
      })))
    },
    extraColumns () {
      return (this.relatedColumns || []).filter((column) => column.type !== 'formula' && column.type !== 'sequence' && column.type !== 'password' && column.type !== 'file')
    }
  },
  methods: {
    emitSettings (next) {
      this.$emit('update:modelValue', normalizeRelationshipSettings(next))
    },
    update (key, value) {
      this.emitSettings({ ...this.settings, [key]: value })
    },
    fieldType (uniqueTitle) {
      const column = (this.relatedColumns || []).find((item) => item.uniqueTitle === uniqueTitle)
      if (!column) return 'text'
      if (column.type === 'relationship') return 'relationship'
      if (column.type === 'number' || column.type === 'sequence' || column.type === 'duration') return 'number'
      return 'text'
    },
    operatorOptions (filter) {
      if (this.fieldType(filter.field) === 'relationship') {
        return [{ value: 'is', text: 'is' }, { value: 'includes', text: 'includes' }]
      }
      return [{ value: '=', text: 'equals' }, { value: 'contains', text: 'contains' }]
    },
    updateFilter (index, key, value) {
      const cascadeFilters = this.settings.cascadeFilters.map((filter, filterIndex) => {
        if (filterIndex !== index) return filter
        const next = { ...filter, [key]: value }
        if (key === 'field') {
          const dataType = this.fieldType(value)
          next.dataType = dataType === 'relationship' ? 'relationship' : dataType === 'number' ? 'number' : 'text'
          next.operator = dataType === 'relationship' ? 'is' : '='
          next.source = 'direct'
        }
        return next
      })
      this.emitSettings({ ...this.settings, cascadeFilters })
    },
    addFilter () {
      this.emitSettings({
        ...this.settings,
        cascadeFilters: this.settings.cascadeFilters.concat([{
          source: 'direct',
          field: '',
          operator: '=',
          dataType: 'text',
          valueFromField: ''
        }])
      })
    },
    removeFilter (index) {
      this.emitSettings({
        ...this.settings,
        cascadeFilters: this.settings.cascadeFilters.filter((_, filterIndex) => filterIndex !== index)
      })
    },
    toggleAddField (uniqueTitle, checked) {
      const addFields = new Set(this.settings.addFields)
      if (checked) addFields.add(uniqueTitle)
      else addFields.delete(uniqueTitle)
      this.emitSettings({ ...this.settings, addFields: [...addFields] })
    }
  }
}
</script>
